import React, { useState, useRef, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';
import { getImageUrl } from '../../lib/utils';

// Extract embed URL for iframes
const getEmbedUrl = (url) => {
  if (!url) return null;
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1`;
  const ytShorts = url.match(/youtube\.com\/shorts\/([^&\s?]+)/);
  if (ytShorts) return `https://www.youtube.com/embed/${ytShorts[1]}?autoplay=1`;
  const vimeo = url.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;
  return null;
};

// Video Modal Component
const VideoModal = ({ video, onClose }) => {
  if (!video) return null;

  const isUploaded = video.video_type === 'upload' && video.video_file;
  const embedUrl = getEmbedUrl(video.video_url);
  const uploadedUrl = isUploaded ? getImageUrl(video.video_file) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm"
      onClick={onClose}
      data-testid="video-modal-overlay"
    >
      <div
        className="relative w-[90vw] max-w-[420px] aspect-[9/16] bg-black rounded-xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="video-modal-content"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-2 bg-black/60 hover:bg-black rounded-full transition-colors"
          data-testid="video-modal-close"
        >
          <X className="h-5 w-5 text-white" />
        </button>

        {isUploaded && uploadedUrl ? (
          <video
            src={uploadedUrl}
            className="w-full h-full object-contain"
            controls
            autoPlay
            playsInline
            data-testid="video-modal-player"
          />
        ) : embedUrl ? (
          <iframe
            src={embedUrl}
            className="w-full h-full border-0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            title={video.title}
            data-testid="video-modal-iframe"
          />
        ) : video.video_url ? (
          <div className="w-full h-full flex flex-col items-center justify-center text-white text-center p-6">
            <Play className="h-12 w-12 mb-4 opacity-50" />
            <p className="text-sm opacity-70 mb-4">This video cannot be embedded</p>
            <a
              href={video.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-white text-black rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
            >
              Open in new tab
            </a>
          </div>
        ) : null}

        {/* Title bar */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/90 to-transparent pointer-events-none">
          <h3 className="text-white font-serif text-base font-semibold leading-tight line-clamp-2">
            {video.title}
          </h3>
          {video.credit && (
            <p className="text-white/60 text-xs mt-1 font-mono">{video.credit}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// Video Card
const VideoCard = ({ video, onClick }) => {
  const isComingSoon = !video.video_url && !video.video_file;
  const thumbnailUrl = video.thumbnail
    ? getImageUrl(video.thumbnail)
    : 'https://images.unsplash.com/photo-1579912437184-595291124a34?w=400&h=710&fit=crop';

  const isUploaded = video.video_type === 'upload' && video.video_file;

  return (
    <div
      className={`group w-[180px] md:w-[200px] flex-shrink-0 snap-start ${!isComingSoon ? 'cursor-pointer' : ''}`}
      data-testid={`trending-video-${video.slot_order}`}
      onClick={!isComingSoon ? onClick : undefined}
    >
      <div className="relative aspect-[9/16] overflow-hidden rounded-lg mb-3 bg-black">
        {isComingSoon ? (
          <div className="w-full h-full bg-gradient-to-br from-zinc-800 to-zinc-900 flex flex-col items-center justify-center text-center p-4">
            <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mb-4">
              <Play className="h-8 w-8 text-white/50" />
            </div>
            <span className="text-white font-serif text-lg font-semibold">Coming Soon</span>
            <span className="text-white/50 text-xs mt-2">New content on the way</span>
          </div>
        ) : (
          <>
            <img
              src={thumbnailUrl}
              alt={video.title}
              className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
              loading="lazy"
              onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1579912437184-595291124a34?w=400&h=710&fit=crop'; }}
            />
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                <Play className="h-5 w-5 text-black ml-0.5" fill="black" />
              </div>
            </div>
          </>
        )}

        {video.duration && !isComingSoon && (
          <span className="absolute bottom-2 right-2 px-2 py-1 bg-black/80 text-white text-xs font-mono rounded">
            {video.duration}
          </span>
        )}

        {isUploaded && (
          <span className="absolute top-2 left-2 px-2 py-1 bg-primary/90 text-white text-xs font-mono rounded">
            Video
          </span>
        )}
      </div>

      <h3 className="font-serif text-sm md:text-base font-semibold leading-tight line-clamp-2 group-hover:text-primary transition-colors">
        {video.title}
      </h3>
      {video.credit && !isComingSoon && (
        <p className="text-background/60 text-xs mt-1 font-mono">{video.credit}</p>
      )}
    </div>
  );
};

// Main Component
export const TrendingVideos = ({ videos = [] }) => {
  const scrollContainerRef = useRef(null);
  const [activeVideo, setActiveVideo] = useState(null);

  const scroll = (direction) => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: direction === 'left' ? -220 : 220,
        behavior: 'smooth'
      });
    }
  };

  const closeModal = useCallback(() => setActiveVideo(null), []);

  // Close on Escape key
  React.useEffect(() => {
    if (!activeVideo) return;
    const handler = (e) => { if (e.key === 'Escape') closeModal(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [activeVideo, closeModal]);

  const activeVideos = videos
    .filter(v => v.is_active)
    .sort((a, b) => a.slot_order - b.slot_order);

  if (activeVideos.length === 0) return null;

  return (
    <section className="py-8 md:py-12 bg-foreground text-background" data-testid="trending-videos">
      <div className="container-custom">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl md:text-3xl font-bold">The Trade Shorts</h2>
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => scroll('left')}
              className="p-2 rounded-sm bg-background/10 hover:bg-background/20 transition-colors"
              aria-label="Scroll left"
              data-testid="trending-scroll-left"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => scroll('right')}
              className="p-2 rounded-sm bg-background/10 hover:bg-background/20 transition-colors"
              aria-label="Scroll right"
              data-testid="trending-scroll-right"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div
          ref={scrollContainerRef}
          className="flex gap-4 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 -mx-4 px-4"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {activeVideos.map((video) => (
            <VideoCard
              key={video.id}
              video={video}
              onClick={() => setActiveVideo(video)}
            />
          ))}
        </div>
      </div>

      {/* Modal */}
      {activeVideo && <VideoModal video={activeVideo} onClose={closeModal} />}
    </section>
  );
};

export default TrendingVideos;
